import React, { useRef, useState } from "react";
import { Box, Button, CircularProgress, Divider, IconButton, Typography } from "@material-ui/core";
import { Add, Delete } from "@material-ui/icons";
import { useForm, useFieldArray, FormProvider } from "react-hook-form";
import { HFlex, ProgressButton, Text, CTextField, useErrorContext } from "shared";
import { useTranslation } from "react-i18next";
import { useCreateMedication, useUpdateMedication } from "../queries";
import { medicationsApi } from "../api";
import { Medication, UpsertMedicationRequest, SUPPORTED_LOCALES, SupportedLocale } from "../types";

interface Props {
  medication?: Medication;
  onSuccess: () => void;
  onCancel: () => void;
}

interface VariantFormData {
  variant_id?: number;
  translations: { locale: SupportedLocale; label: string }[];
}

interface FormData {
  semantic_key: string;
  translations: { locale: SupportedLocale; name: string; description: string }[];
  variants: VariantFormData[];
}

function buildDefaultTranslations(): FormData["translations"] {
  return SUPPORTED_LOCALES.map(locale => ({ locale, name: "", description: "" }));
}

function buildDefaultVariantTranslations(): VariantFormData["translations"] {
  return SUPPORTED_LOCALES.map(locale => ({ locale, label: "" }));
}

function buildDefaultValues(medication?: Medication): FormData {
  if (!medication) {
    return {
      semantic_key: "",
      translations: buildDefaultTranslations(),
      variants: [{ translations: buildDefaultVariantTranslations() }],
    };
  }

  const translations = SUPPORTED_LOCALES.map(locale => {
    const existing = medication.translations.find(t => t.locale === locale);
    return {
      locale,
      name: existing?.name ?? "",
      description: existing?.description ?? "",
    };
  });

  const variants: VariantFormData[] =
    medication.variants.length > 0
      ? medication.variants.map(v => ({
          variant_id: v.id,
          translations: SUPPORTED_LOCALES.map(locale => {
            const existing = v.translations.find(t => t.locale === locale);
            return { locale, label: existing?.label ?? "" };
          }),
        }))
      : [{ translations: buildDefaultVariantTranslations() }];

  return { semantic_key: medication.semantic_key ?? "", translations, variants };
}

export const MedicationForm: React.VFC<Props> = ({ medication, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const setError = useErrorContext();
  const isEditing = !!medication;
  const createMutation = useCreateMedication();
  const updateMutation = useUpdateMedication();
  const isLoading = createMutation.isLoading || updateMutation.isLoading;

  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>(medication?.image_url ?? "");
  const [imageUrl, setImageUrl] = useState<string>(medication?.image_url ?? "");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [localeIsUploading, setLocaleIsUploading] = useState<Record<string, boolean>>({});
  const [localePreviewUrls, setLocalePreviewUrls] = useState<Record<string, string>>(() => {
    const urls: Record<string, string> = {};
    if (medication) {
      medication.translations.forEach(t => {
        if (t.image_url) urls[t.locale] = t.image_url;
      });
    }
    return urls;
  });
  const [localeImageUrls, setLocaleImageUrls] = useState<Record<string, string>>(() => {
    const urls: Record<string, string> = {};
    if (medication) {
      medication.translations.forEach(t => {
        if (t.image_url) urls[t.locale] = t.image_url;
      });
    }
    return urls;
  });
  const localeFileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const form = useForm<FormData>({
    defaultValues: buildDefaultValues(medication),
  });

  const { fields: variantFields, append: appendVariant, remove: removeVariant } = useFieldArray({
    control: form.control,
    name: "variants",
  });

  const handleImageUpload = async (file: File) => {
    let ext: string;
    if (file.type === "image/jpeg") {
      ext = ".jpg";
    } else if (file.type === "image/png") {
      ext = ".png";
    } else {
      setError(t("medications.error_unsupported_file"));
      return;
    }

    setPreviewUrl(URL.createObjectURL(file));
    setIsUploading(true);

    try {
      const presign = await medicationsApi.presignImageUpload(file.type);

      const formData = new FormData();
      Object.entries(presign.upload_fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append("file", file, `default${ext}`);

      const uploadRes = await fetch(presign.upload_url, {
        method: "POST",
        body: formData,
      });

      if (!uploadRes.ok) {
        throw new Error("Failed to upload image to S3");
      }

      setImageUrl(presign.object_url);
    } catch (err) {
      setError(t("medications.error_upload_failed"), err);
      setPreviewUrl(imageUrl);
    } finally {
      setIsUploading(false);
    }
  };

  const handleLocaleImageUpload = async (locale: string, file: File) => {
    let ext: string;
    if (file.type === "image/jpeg") {
      ext = ".jpg";
    } else if (file.type === "image/png") {
      ext = ".png";
    } else {
      setError(t("medications.error_unsupported_file"));
      return;
    }

    setLocalePreviewUrls(prev => ({ ...prev, [locale]: URL.createObjectURL(file) }));
    setLocaleIsUploading(prev => ({ ...prev, [locale]: true }));

    try {
      const presign = await medicationsApi.presignImageUpload(file.type, locale);

      const formData = new FormData();
      Object.entries(presign.upload_fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append("file", file, `${locale}${ext}`);

      const uploadRes = await fetch(presign.upload_url, {
        method: "POST",
        body: formData,
      });

      if (!uploadRes.ok) {
        throw new Error("Failed to upload image to S3");
      }

      setLocaleImageUrls(prev => ({ ...prev, [locale]: presign.object_url }));
    } catch (err) {
      setError(t("medications.error_upload_failed"), err);
      setLocalePreviewUrls(prev => {
        const next = { ...prev };
        const existingUrl = localeImageUrls[locale];
        if (existingUrl) {
          next[locale] = existingUrl;
        } else {
          delete next[locale];
        }
        return next;
      });
    } finally {
      setLocaleIsUploading(prev => ({ ...prev, [locale]: false }));
    }
  };

  const handleRemoveLocaleImage = (locale: string) => {
    setLocalePreviewUrls(prev => {
      const next = { ...prev };
      delete next[locale];
      return next;
    });
    setLocaleImageUrls(prev => {
      const next = { ...prev };
      delete next[locale];
      return next;
    });
  };

  const onSubmit = form.handleSubmit(async data => {
    const filledTranslations = data.translations.filter(t => t.name.trim() !== "");
    if (filledTranslations.length === 0) {
      setError(t("medications.error_translation_required"));
      return;
    }

    if (!imageUrl) {
      setError(t("medications.error_default_image_required"));
      return;
    }

    const request: UpsertMedicationRequest = {
      image_url: imageUrl,
      semantic_key: data.semantic_key.trim() || null,
      translations: filledTranslations.map(t => ({
        locale: t.locale,
        name: t.name.trim(),
        description: t.description.trim() || undefined,
        image_url: localeImageUrls[t.locale] || undefined,
      })),
      variants: (data.variants ?? [])
        .map(v => ({
          id: v.variant_id ? Number(v.variant_id) : undefined,
          translations: v.translations
            .filter(t => t.label.trim() !== "")
            .map(t => ({ locale: t.locale, label: t.label.trim() })),
        }))
        .filter(v => v.translations.length > 0),
    };

    try {
      if (isEditing) {
        await updateMutation.mutateAsync({ id: medication.id, data: request });
      } else {
        await createMutation.mutateAsync(request);
      }
      onSuccess();
    } catch (err) {
      setError(t(isEditing ? "medications.error_update_failed" : "medications.error_create_failed"), err);
    }
  });

  return (
    <FormProvider {...form}>
      <Box mx={3} my={2}>
        <Text heading bold mb={2}>
          {t(isEditing ? "medications.edit_medication" : "medications.create_medication")}
        </Text>

        <form onSubmit={onSubmit}>
          <Box mb={2}>
            <CTextField
              name="semantic_key"
              label={t("medications.semantic_key")}
              size="small"
              fullWidth
              margin="dense"
              defaultValue={medication?.semantic_key ?? ""}
            />
          </Box>

          <Typography variant="subtitle1" gutterBottom>
            <strong>{t("medications.translations")}</strong>
          </Typography>
          {SUPPORTED_LOCALES.map((locale, idx) => {
            const existingTranslation = medication?.translations.find(tr => tr.locale === locale);
            return (
              <Box key={locale} mb={1}>
                <input type="hidden" name={`translations[${idx}].locale`} defaultValue={locale} ref={form.register()} />
                <Typography variant="caption" color="textSecondary">
                  {locale}
                </Typography>
                <HFlex gap={2} alignItems="flex-start">
                  <CTextField
                    name={`translations[${idx}].name`}
                    label={t("medications.name")}
                    size="small"
                    fullWidth
                    margin="dense"
                    defaultValue={existingTranslation?.name ?? ""}
                  />
                  <CTextField
                    name={`translations[${idx}].description`}
                    label={t("medications.description")}
                    size="small"
                    fullWidth
                    margin="dense"
                    defaultValue={existingTranslation?.description ?? ""}
                  />
                </HFlex>
                <HFlex gap={1} alignItems="center" mt={0.5} mb={0.5}>
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    style={{ display: "none" }}
                    ref={el => {
                      localeFileInputRefs.current[locale] = el;
                    }}
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) handleLocaleImageUpload(locale, file);
                      e.target.value = "";
                    }}
                  />
                  <Button
                    type="button"
                    variant="text"
                    size="small"
                    disabled={localeIsUploading[locale]}
                    onClick={() => localeFileInputRefs.current[locale]?.click()}
                    startIcon={localeIsUploading[locale] ? <CircularProgress size={12} /> : undefined}
                  >
                    {localeIsUploading[locale]
                      ? t("medications.uploading")
                      : localeImageUrls[locale]
                      ? t("medications.replace_locale_image")
                      : t("medications.upload_locale_image")}
                  </Button>
                  {localePreviewUrls[locale] ? (
                    <>
                      <img
                        src={localePreviewUrls[locale]}
                        alt={`${locale} medication`}
                        style={{
                          height: 36,
                          width: 36,
                          objectFit: "contain",
                          borderRadius: 4,
                          border: "1px solid #e0e0e0",
                        }}
                      />
                      <IconButton
                        type="button"
                        size="small"
                        onClick={() => handleRemoveLocaleImage(locale)}
                        title={t("medications.remove_locale_image")}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <Typography variant="caption" color="textSecondary">
                      {t("medications.using_default_image")}
                    </Typography>
                  )}
                </HFlex>
              </Box>
            );
          })}

          <Divider style={{ margin: "16px 0" }} />

          <Typography variant="subtitle1" gutterBottom>
            <strong>{t("medications.default_image")}</strong>
          </Typography>
          <HFlex gap={2} alignItems="center" mb={1}>
            <input
              type="file"
              accept="image/jpeg,image/png"
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) {
                  handleImageUpload(file);
                }
                e.target.value = "";
              }}
            />
            <Button
              type="button"
              variant="outlined"
              size="small"
              disabled={isUploading}
              onClick={() => fileInputRef.current?.click()}
              startIcon={isUploading ? <CircularProgress size={14} /> : undefined}
            >
              {isUploading
                ? t("medications.uploading")
                : imageUrl
                ? t("medications.replace_image")
                : t("medications.upload_image")}
            </Button>
            {(previewUrl || imageUrl) && (
              <img
                src={previewUrl || imageUrl}
                alt="Default medication"
                style={{
                  height: 48,
                  width: 48,
                  objectFit: "contain",
                  borderRadius: 4,
                  border: "1px solid #e0e0e0",
                }}
              />
            )}
          </HFlex>

          <Divider style={{ margin: "16px 0" }} />

          <HFlex justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1">
              <strong>{t("medications.variants")}</strong>
            </Typography>
            <Button
              type="button"
              size="small"
              startIcon={<Add />}
              onClick={() => appendVariant({ translations: buildDefaultVariantTranslations() })}
            >
              {t("medications.add_variant")}
            </Button>
          </HFlex>

          {variantFields.map((variantField, variantIdx) => (
            <Box key={variantField.id} mb={3} p={2} style={{ border: "1px solid #e0e0e0", borderRadius: 4 }}>
              <input
                type="hidden"
                name={`variants[${variantIdx}].variant_id`}
                defaultValue={variantFields[variantIdx]?.variant_id ?? ""}
                ref={form.register()}
              />
              <HFlex justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2">
                  <strong>{t("medications.variant_number", { number: variantIdx + 1 })}</strong>
                </Typography>
                <IconButton type="button" size="small" onClick={() => removeVariant(variantIdx)}>
                  <Delete fontSize="small" />
                </IconButton>
              </HFlex>

              <Box mt={1}>
                <Typography variant="caption" color="textSecondary">
                  {t("medications.labels_per_locale")}
                </Typography>
                {SUPPORTED_LOCALES.map((locale, localeIdx) => (
                  <HFlex key={locale} gap={2} alignItems="center">
                    <input
                      type="hidden"
                      name={`variants[${variantIdx}].translations[${localeIdx}].locale`}
                      defaultValue={locale}
                      ref={form.register()}
                    />
                    <Typography variant="caption" style={{ minWidth: 60 }}>
                      {locale}
                    </Typography>
                    <CTextField
                      name={`variants[${variantIdx}].translations[${localeIdx}].label`}
                      label={t("medications.label")}
                      size="small"
                      fullWidth
                      margin="dense"
                      defaultValue={variantFields[variantIdx]?.translations?.[localeIdx]?.label ?? ""}
                    />
                  </HFlex>
                ))}
              </Box>
            </Box>
          ))}

          <HFlex justifyContent="flex-end" gap={2} my={3}>
            <Button type="button" disabled={isLoading} onClick={onCancel}>
              {t("buttons.cancel")}
            </Button>
            <ProgressButton loading={isLoading}>
              {t(isEditing ? "medications.save_changes" : "medications.create")}
            </ProgressButton>
          </HFlex>
        </form>
      </Box>
    </FormProvider>
  );
};
